import React, { FC } from "react";
import FadeIn from "react-fade-in";
import {
  FiMusic,
  FiZap,
  FiClock,
  FiLayers,
  FiFileText,
} from "react-icons/fi";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  ScatterController,
} from "chart.js";
import { Line, Bar, Scatter, Chart } from "react-chartjs-2";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ScatterController,
  Title,
  Tooltip,
  Legend,
  Filler
);

interface TranscriptionOverviewProps {
  startDate: string;
  endDate: string;
  refreshTrigger?: number;
}

const TranscriptionOverview: FC<TranscriptionOverviewProps> = ({
  startDate,
  endDate,
  refreshTrigger,
}) => {
  // Mock KPI data
  const kpiData = [
    {
      label: "Total Jobs",
      value: "304",
      icon: <FiFileText size={18} />,
      bgColor: "bg-blue-50",
      iconColor: "text-blue-500",
    },
    {
      label: "Total Audio",
      value: "8.19 hrs",
      icon: <FiMusic size={18} />,
      bgColor: "bg-green-50",
      iconColor: "text-green-500",
    },
    {
      label: "Throughput",
      value: "100.0%",
      icon: <FiZap size={18} />,
      bgColor: "bg-pink-50",
      iconColor: "text-pink-500",
    },
    {
      label: "Avg Latency",
      value: "6.37 s",
      icon: <FiClock size={18} />,
      bgColor: "bg-purple-50",
      iconColor: "text-purple-500",
    },
    {
      label: "Avg Queue Wait",
      value: "0 s",
      icon: <FiLayers size={18} />,
      bgColor: "bg-orange-50",
      iconColor: "text-orange-500",
    },
  ];

  const labels = ["05:00", "06:00", "07:00", "08:00", "09:00", "10:00", "11:00", "12:00", "13:00", "14:00", "15:00", "16:00"];

  // 1. Jobs Throughput (Hourly)
  const throughputData = {
    labels,
    datasets: [
      {
        fill: true,
        label: "Throughput",
        data: [25, 55, 45, 30, 20, 15, 25, 12, 18, 18, 12, 4],
        borderColor: "#3b82f6",
        backgroundColor: "rgba(59, 130, 246, 0.1)",
        tension: 0.4,
        pointRadius: 0,
      },
    ],
  };

  // 2. Queue Wait Times (Hourly)
  const queueWaitData = {
    labels,
    datasets: [
      {
        label: "STT Queue",
        data: [0, 0.017, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
        borderColor: "#4f46e5",
        borderWidth: 2,
        pointRadius: 0,
        tension: 0.4,
      },
      {
        label: "Analytics Queue",
        data: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
        borderColor: "#f59e0b",
        borderWidth: 2,
        pointRadius: 0,
        tension: 0.4,
      },
      {
        label: "Webhook Delivery",
        data: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
        borderColor: "#10b981",
        borderWidth: 2,
        pointRadius: 0,
        tension: 0.4,
      },
    ],
  };

  // 3. Audio Duration Distribution
  const durationLabels = ["0-60s", "120-180s", "240-300s", "360-420s", "480-540s", "600-660s", "2280-2340s"];
  const durationDistData = {
    labels: durationLabels,
    datasets: [
      {
        label: "Audio Samples",
        data: [190, 45, 35, 15, 5, 8, 3],
        backgroundColor: "#8b5cf6",
        borderRadius: 4,
        barThickness: 30,
      },
    ],
  };

  // 4. Avg processing Time Comparison (Horizontal Bar)
  const processingTimeData = {
    labels: ["Webhook", "Analytics", "STT"],
    datasets: [
      {
        label: "Time (s)",
        data: [0.01, 3.8, 1.8],
        backgroundColor: "#ec4899",
        borderRadius: 4,
        barThickness: 25,
      },
    ],
  };

  // 5. Duration vs STT processing Time
  const scatterData = {
    datasets: [
      {
        label: "Duration vs STT Time",
        data: Array.from({ length: 50 }, () => ({
          x: Math.random() * 600,
          y: Math.random() * 10,
        })).concat([
            { x: 2300, y: 22 }
        ]),
        backgroundColor: "#0ea5e9",
      },
    ],
  };

  // 6. Latency Percentiles (P50, P95, P99)
  const latencyData = {
    labels,
    datasets: [
      {
        label: "P50",
        data: [5, 4, 4, 5, 4, 7, 8, 6, 8, 5, 5, 6],
        borderColor: "#10b981",
        borderWidth: 2,
        pointRadius: 0,
        tension: 0.4,
      },
      {
        label: "P95",
        data: [18, 12, 14, 11, 11, 20, 14, 19, 15, 10, 14, 10],
        borderColor: "#f59e0b",
        borderWidth: 2,
        pointRadius: 0,
        tension: 0.4,
      },
      {
        label: "P99",
        data: [20, 38, 25, 12, 20, 22, 14, 19, 16, 11, 14, 10],
        borderColor: "#ef4444",
        borderWidth: 2,
        pointRadius: 0,
        tension: 0.4,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "bottom" as const,
        labels: {
          usePointStyle: true,
          padding: 20,
          font: { size: 12 },
        },
      },
      tooltip: {
        mode: "index" as const,
        intersect: false,
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        grid: {
          borderDash: [5, 5],
          color: "rgba(0, 0, 0, 0.05)",
        },
      },
      x: {
        grid: {
          display: false,
        },
      },
    },
  };

  const horizontalBarOptions = {
    ...chartOptions,
    indexAxis: 'y' as const,
    scales: {
        ...chartOptions.scales,
        x: {
            beginAtZero: true,
            title: { display: true, text: 's', position: 'end' as const }
        }
    }
  };

  return (
    <div className="flex flex-col gap-6 w-full">
      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
        {kpiData.map((kpi, idx) => (
          <FadeIn key={idx} delay={idx * 50}>
            <div className="bg-white p-5 rounded-lg shadow-lg border border-[#e5dddd92] flex flex-col justify-between h-full group hover:shadow-xl transition-all duration-300">
              <div className="flex justify-between items-start mb-4">
                <div className="text-sm font-medium text-[#64748b]">
                  {kpi.label}
                </div>
                <div className="p-2 bg-[#dbeafe] text-blue-600 rounded-full">
                  {kpi.icon}
                </div>
              </div>
              <div>
                <h3 className="text-3xl font-bold text-gray-800">{kpi.value}</h3>
                <div className="mt-2 text-xs text-[#64748b] flex items-center gap-1">
                  <span className="text-[green] font-medium">↑ 12%</span>
                  <span>vs last period</span>
                </div>
              </div>
            </div>
          </FadeIn>
        ))}
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-4">
        {/* Row 1 */}
        <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
            <h3 className="text-base font-bold text-gray-800 mb-6">Jobs Throughput (Hourly)</h3>
            <div className="h-[300px]">
                <Line data={throughputData} options={chartOptions} />
            </div>
        </div>
        <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
            <h3 className="text-base font-bold text-gray-800 mb-6">Queue Wait Times (Hourly)</h3>
            <div className="h-[300px]">
                <Line data={queueWaitData} options={chartOptions} />
            </div>
        </div>

        {/* Row 2 */}
        <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
            <h3 className="text-base font-bold text-gray-800 mb-6">Audio Duration Distribution</h3>
            <div className="h-[300px]">
                <Bar data={durationDistData} options={chartOptions} />
            </div>
        </div>
        <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
            <h3 className="text-base font-bold text-gray-800 mb-6">Avg processing Time Comparison</h3>
            <div className="h-[300px]">
                <Bar data={processingTimeData} options={horizontalBarOptions as any} />
            </div>
        </div>

        {/* Row 3 */}
        <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 lg:col-span-2">
            <h3 className="text-base font-bold text-gray-800 mb-6">Duration vs STT processing Time</h3>
            <div className="h-[300px]">
                <Scatter 
                    data={scatterData} 
                    options={{
                        ...chartOptions,
                        scales: {
                            ...chartOptions.scales,
                            x: {
                                type: 'linear',
                                position: 'bottom',
                                title: { display: true, text: 'Audio Duration (s)', font: { size: 10 } },
                            },
                            y: {
                                title: { display: true, text: 'STT Time (s)', font: { size: 10 } },
                            }
                        }
                    } as any} 
                />
            </div>
        </div>
        {/* <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
            <h3 className="text-base font-bold text-gray-800 mb-6">Latency Percentiles (P50, P95, P99)</h3>
            <div className="h-[300px]">
                <Line data={latencyData} options={chartOptions} />
            </div>
        </div> */}
      </div>
    </div>
  );
};

export default TranscriptionOverview;
